import React, { useState } from "react";
import {
  AppBar,
  Avatar,
  Badge,
  Box,
  Chip,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { toast } from "react-toastify";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import VpnKeyOutlinedIcon from "@mui/icons-material/VpnKeyOutlined";
import FullscreenOutlinedIcon from "@mui/icons-material/FullscreenOutlined";
import FullscreenExitOutlinedIcon from "@mui/icons-material/FullscreenExitOutlined";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useThemeMode } from "../../context/ThemeContext";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import NotificationBell from "./NotificationBell";
import { useNotifications } from "../../context/NotificationContext";
import { enablePushNotifications, pushSupport } from "../../utils/pushNotifications";

export default function AppTopbar({ onMenuClick, leftOffset = 0 }) {
  const { user, logout } = useAuth();
  const { chatUnread } = useNotifications();
  const { mode, toggle } = useThemeMode();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isDark = mode === "dark";
  const [anchor, setAnchor] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [pushState, setPushState] = useState(pushSupport()); // unsupported | default | granted | denied

  const handleEnablePush = async () => {
    setAnchor(null);
    const result = await enablePushNotifications();
    setPushState(pushSupport());
    if (result === "enabled") {
      toast.success("Device notifications enabled — a test notification is on its way");
    } else if (result === "denied") {
      toast.warn("Notifications are blocked for this site — allow them in your browser settings");
    } else if (result === "unsupported") {
      toast.info(
        /iphone|ipad/i.test(navigator.userAgent)
          ? "On iPhone: open this site in Safari → Share → Add to Home Screen, then enable notifications from that app"
          : "This browser does not support push notifications"
      );
    } else {
      toast.error(result.replace(/^error:/, "Could not enable: "));
    }
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch(() => {});
      setIsFullscreen(true);
    } else {
      document.exitFullscreen?.();
      setIsFullscreen(false);
    }
  };

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        left: { md: leftOffset },
        width: { md: `calc(100% - ${leftOffset}px)` },
        bgcolor: "background.default",
        borderBottom: "1px solid",
        borderColor: "divider",
        zIndex: (t) => t.zIndex.drawer + 1,
        color: "text.primary",
      }}
    >
      <Toolbar sx={{ minHeight: 64, px: { xs: 2, md: 3 }, gap: 1 }}>
        {/* Menu toggle — always visible so users can open/close the sidebar on any screen size */}
        <Tooltip title="Toggle sidebar" arrow>
          <IconButton edge="start" onClick={onMenuClick} sx={{ color: "text.secondary", mr: 1 }}>
            <MenuIcon />
          </IconButton>
        </Tooltip>

        {/* Brand wordmark on mobile — the sidebar logo is hidden when the drawer
            is closed, so without this the top bar has no identity and a barren
            empty centre. Desktop keeps the sidebar brand, so only show on mobile. */}
        {isMobile && (
          <Stack direction="row" alignItems="center" spacing={1}>
            <Box
              sx={{
                width: 30, height: 30, borderRadius: 1.5,
                background: "linear-gradient(135deg, #6C50BF 0%, #492ba3 100%)",
                display: "flex", alignItems: "center", justifyContent: "center",
                color: "white", fontWeight: 800, fontSize: 13,
              }}
            >
              IU
            </Box>
            <Typography sx={{ fontWeight: 800, fontSize: 16, color: "text.primary", letterSpacing: "-0.01em" }}>
              InfyU
            </Typography>
          </Stack>
        )}

        <Box sx={{ flex: 1 }} />

        {/* Right cluster */}
        <Stack direction="row" spacing={0.5} alignItems="center">
          {!isMobile && (
            <>
              <Tooltip title="Help Center" arrow>
                <IconButton onClick={() => navigate("/helpdesk")} aria-label="Help Center" sx={{ color: "text.secondary" }} size="small">
                  <HelpOutlineOutlinedIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"} arrow>
                <IconButton onClick={toggleFullscreen} sx={{ color: "text.secondary" }} size="small">
                  {isFullscreen ? <FullscreenExitOutlinedIcon sx={{ fontSize: 20 }} /> : <FullscreenOutlinedIcon sx={{ fontSize: 20 }} />}
                </IconButton>
              </Tooltip>
            </>
          )}
          <Tooltip title={isDark ? "Light mode" : "Dark mode"} arrow>
            <IconButton onClick={toggle} sx={{ color: "text.secondary" }} size="small">
              {isDark ? <LightModeOutlinedIcon sx={{ fontSize: 20 }} /> : <DarkModeOutlinedIcon sx={{ fontSize: 20 }} />}
            </IconButton>
          </Tooltip>
          <Tooltip title="Team chat" arrow>
            <IconButton
              onClick={() => navigate("/chatboard")}
              sx={{ color: "text.secondary" }}
              size="small"
              aria-label={chatUnread ? `Team chat, ${chatUnread} unread` : "Team chat"}
            >
              <Badge badgeContent={chatUnread} color="error" max={99}>
                <ChatBubbleOutlineIcon sx={{ fontSize: 20 }} />
              </Badge>
            </IconButton>
          </Tooltip>
          <NotificationBell />

          <Divider orientation="vertical" flexItem sx={{ mx: 1, my: 1 }} />

          <Box
            onClick={(e) => setAnchor(e.currentTarget)}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              px: 1,
              py: 0.5,
              borderRadius: 2,
              cursor: "pointer",
              "&:hover": { bgcolor: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.04)" },
            }}
          >
            <Badge
              overlap="circular"
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              badgeContent={
                <Box sx={{ width: 10, height: 10, bgcolor: "success.main", border: "2px solid", borderColor: "background.default", borderRadius: "50%" }} />
              }
            >
              <Avatar
                src={user?.profilePicture || undefined}
                sx={{
                  width: 34,
                  height: 34,
                  background: "linear-gradient(135deg, #6C50BF 0%, #492ba3 100%)",
                  color: "white",
                  fontSize: 13,
                  fontWeight: 700,
                }}
              >
                {(user?.name || "U").slice(0, 1).toUpperCase()}
              </Avatar>
            </Badge>
            {!isMobile && (
              <Box sx={{ minWidth: 0, maxWidth: 160 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 600, lineHeight: 1.2 }} noWrap>
                  {user?.name}
                </Typography>
                <Typography sx={{ fontSize: 11, color: "text.secondary", textTransform: "capitalize" }} noWrap>
                  {user?.role}
                </Typography>
                {user?.employeeid && (
                  <Typography sx={{ fontSize: 10, color: "text.secondary", fontWeight: 600 }} noWrap>
                    {user.employeeid}
                  </Typography>
                )}
              </Box>
            )}
          </Box>

          <Menu
            anchorEl={anchor}
            open={Boolean(anchor)}
            onClose={() => setAnchor(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            PaperProps={{
              sx: {
                mt: 1,
                minWidth: 240,
                borderRadius: 3,
                border: "1px solid",
                borderColor: "divider",
                boxShadow: isDark ? "0 10px 40px rgba(0,0,0,0.5)" : "0 10px 40px rgba(0,0,0,0.1)",
              },
            }}
          >
            <Box sx={{ px: 2, py: 1.5 }}>
              <Typography sx={{ fontWeight: 700, fontSize: 14 }}>{user?.name}</Typography>
              {user?.employeeid && (
                <Typography sx={{ fontSize: 11, color: "text.secondary", fontWeight: 600 }}>
                  ID: {user.employeeid}
                </Typography>
              )}
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Typography sx={{ fontSize: 12, color: "text.secondary", userSelect: "text", cursor: "text" }} noWrap>{user?.email}</Typography>
                {user?.email && (
                  <Tooltip title="Copy email" arrow>
                    <IconButton
                      size="small"
                      onClick={() => { navigator.clipboard?.writeText(user.email); toast.success("Email copied"); }}
                      sx={{ p: 0.25, color: "text.secondary" }}
                    >
                      <ContentCopyIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
              <Chip
                label={user?.role}
                size="small"
                color="primary"
                sx={{ mt: 1, height: 20, fontSize: 10, fontWeight: 700, textTransform: "uppercase" }}
              />
            </Box>
            <Divider />
            <MenuItem onClick={() => { setAnchor(null); navigate("/me"); }} sx={{ py: 1.25 }}>
              <AccountCircleOutlinedIcon sx={{ fontSize: 18, mr: 1.5, color: "text.secondary" }} />
              My Profile
            </MenuItem>
            <MenuItem onClick={() => { setAnchor(null); navigate("/security/2fa"); }} sx={{ py: 1.25 }}>
              <VpnKeyOutlinedIcon sx={{ fontSize: 18, mr: 1.5, color: "text.secondary" }} />
              Security
            </MenuItem>
            <MenuItem onClick={handleEnablePush} sx={{ py: 1.25 }}>
              <NotificationsActiveOutlinedIcon
                sx={{ fontSize: 18, mr: 1.5, color: pushState === "granted" ? "success.main" : "text.secondary" }}
              />
              {pushState === "granted" ? "Device notifications: On" : "Enable device notifications"}
            </MenuItem>
            <MenuItem onClick={() => { setAnchor(null); navigate("/ResetPassword"); }} sx={{ py: 1.25 }}>
              <SettingsOutlinedIcon sx={{ fontSize: 18, mr: 1.5, color: "text.secondary" }} />
              Change Password
            </MenuItem>
            <Divider />
            <MenuItem onClick={logout} sx={{ py: 1.25, color: "error.main" }}>
              <LogoutOutlinedIcon sx={{ fontSize: 18, mr: 1.5 }} />
              Logout
            </MenuItem>
          </Menu>
        </Stack>
      </Toolbar>
    </AppBar>
  );
}
